import { useState } from 'react';
import { useLocation } from 'wouter';
import { useSetPassword } from './passwordSignIn.js';

const MIN_LENGTH = 12;
const MAX_LENGTH = 128;

/**
 * The token from the sign-in link, `/set-password#<token>`: in the fragment,
 * which the browser never sends, so no server or log on the way sees it.
 */
function tokenFromFragment(): string {
  try {
    return decodeURIComponent(window.location.hash.slice(1));
  } catch {
    return '';
  }
}

/** Where a recruiter chooses their password, from the link Project A handed them; no session. */
export function SetPasswordPage(): React.JSX.Element {
  const [token] = useState(tokenFromFragment);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [, navigate] = useLocation();
  const setUp = useSetPassword();

  return (
    <div className="signin">
      <div className="signin-card">
        <h1>Talent Pool</h1>
        {token === '' ? (
          <p>This link is not valid. Ask Project A for a new sign-in link.</p>
        ) : (
          <form
            className="signin-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (password !== confirmation) {
                setMismatch(true);
                return;
              }
              setMismatch(false);
              // Off the link's page once it is used: the fragment's token is spent.
              setUp.mutate(
                { token, password },
                { onSuccess: () => navigate('/', { replace: true }) },
              );
            }}
          >
            <p className="subtle">
              Choose a password of {MIN_LENGTH} to {MAX_LENGTH} characters. A few unrelated words
              make a good one.
            </p>
            <input
              type="password"
              required
              autoComplete="new-password"
              aria-label="New password"
              placeholder="New password"
              minLength={MIN_LENGTH}
              maxLength={MAX_LENGTH}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <input
              type="password"
              required
              autoComplete="new-password"
              aria-label="New password again"
              placeholder="New password again"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
            <button className="button primary" type="submit" disabled={setUp.isPending}>
              Set password and sign in
            </button>
            {mismatch && <p className="signin-error">The two passwords are not the same.</p>}
            {setUp.isError && <p className="signin-error">{setUp.error.message}</p>}
          </form>
        )}
      </div>
    </div>
  );
}
